import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import Icon from '../../components/AppIcon';

const ForStudents = () => {
  const { user, userProfile, isAuthenticated } = useAuth();

  const learningBenefits = [
    {
      icon: 'Gamepad2',
      title: 'Gamified Learning',
      description: 'Earn XP, unlock badges, and compete with classmates as you master Shakespeare\'s plays through engaging challenges and interactive quests.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: 'Target',
      title: 'Pronunciation Practice',
      description: 'Perfect your Shakespearean diction with audio comparisons, instant feedback, and AI-powered pronunciation coaching.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: 'BookMarked',
      title: 'Interactive Glossary',
      description: 'Explore comprehensive definitions, historical context, and modern translations for every challenging word and phrase.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: 'Brain',
      title: 'Comprehensive Quizzes',
      description: 'Test your understanding with reading comprehension, character analysis, and story structure questions designed for success.',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const progressFeatures = [
    {
      icon: 'Trophy',
      title: 'Achievement Gallery',
      description: 'Showcase your badges, track milestones, and celebrate your Shakespeare learning journey with visual progress indicators.',
      stats: '50+ Badges'
    },
    {
      icon: 'TrendingUp',
      title: 'Learning Streaks',
      description: 'Build consistent study habits with daily streak tracking and motivational reminders that keep you engaged.',
      stats: 'Daily Goals'
    },
    {
      icon: 'Medal',
      title: 'Leaderboards',
      description: 'Compete with classmates, climb rankings, and showcase your expertise through friendly academic competition.',
      stats: 'Class Rankings'
    },
    {
      icon: 'BookOpen',
      title: 'Personalized Path',
      description: 'AI-powered recommendations guide your learning journey based on your progress, strengths, and areas for improvement.',
      stats: 'Smart Learning'
    }
  ];

  const studyTools = [
    {
      icon: 'Bookmark',
      title: 'Smart Bookmarking',
      description: 'Save important scenes, favorite quotes, and challenging passages for quick review and exam preparation.'
    },
    {
      icon: 'Library',
      title: 'Vocabulary Builder',
      description: 'Build your Shakespeare vocabulary with spaced repetition flashcards and contextual learning exercises.'
    },
    {
      icon: 'Users',
      title: 'Collaborative Study',
      description: 'Connect with classmates, share insights, and learn together through group challenges and discussions.'
    },
    {
      icon: 'Lightbulb',
      title: 'AI Study Assistant',
      description: 'Get instant answers to questions, personalized study tips, and adaptive learning recommendations.'
    }
  ];

  const studentTestimonials = [
    {
      quote: "BardPlay transformed Shakespeare from my most dreaded subject into something I actually look forward to!",
      author: "Sarah M.",
      grade: "10th Grade",
      achievement: "Completed 3 plays with 95% average"
    },
    {
      quote: "The gamification made studying feel like playing a video game. I finally understand Romeo and Juliet!",
      author: "Marcus J.",
      grade: "9th Grade",
      achievement: "45-day learning streak"
    },
    {
      quote: "I love competing with my friends on the leaderboard. We all improved our grades together!",
      author: "Emily R.",
      grade: "11th Grade",
      achievement: "Top 5 in class rankings"
    }
  ];

  return (
    <>
      <Helmet>
        <title>For Students - BardPlay</title>
        <meta name="description" content="Transform your Shakespeare learning experience with BardPlay's gamified platform. Earn XP, unlock achievements, and master classic literature through engaging interactive tools." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-background via-surface-dark to-background">
        <RoleBasedNavigation 
          user={{
            ...user,
            role: userProfile?.role,
            full_name: userProfile?.full_name,
            email: user?.email
          }} 
          isAuthenticated={isAuthenticated} 
        />
        
        <main className="main-content pt-24 pb-16">
          {/* Hero Section */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-6 border border-primary/20">
                <Icon name="Sparkles" size={20} className="text-primary" />
                <span className="text-primary font-semibold">Level Up Your Shakespeare Skills</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Master Shakespeare Through <span className="text-gradient">Gamified Learning</span>
              </h1>
              <p className="text-xl text-white/70 leading-relaxed mb-8">
                Transform challenging literature into an exciting adventure. Earn XP, unlock badges, and compete with friends while mastering the world's greatest plays.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/registration"
                  className="group px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Start Learning Free
                  <Icon name="ArrowRight" size={20} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <button className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors duration-300 border border-white/20">
                  Watch Demo
                </button>
              </div>
            </div>
          </div>

          {/* Learning Benefits */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Why Students Love BardPlay
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                Discover how our platform makes Shakespeare accessible, engaging, and actually fun to learn
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {learningBenefits?.map((benefit, index) => (
                <div 
                  key={index}
                  className="group bg-surface-dark/50 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-primary/30 transition-all duration-300 hover:scale-[1.02]"
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${benefit?.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon name={benefit?.icon} size={28} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">{benefit?.title}</h3>
                  <p className="text-white/70 leading-relaxed">{benefit?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Progress Tracking */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-3xl p-8 md:p-12 border border-white/10">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Track Your Progress
                </h2>
                <p className="text-white/70 max-w-2xl mx-auto">
                  Visualize your achievements and stay motivated with comprehensive progress tracking
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {progressFeatures?.map((feature, index) => (
                  <div 
                    key={index}
                    className="bg-surface-dark/80 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-primary/30 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
                      <Icon name={feature?.icon} size={24} className="text-primary" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{feature?.title}</h3>
                    <p className="text-white/60 text-sm mb-3">{feature?.description}</p>
                    <div className="flex items-center gap-2 text-primary">
                      <Icon name="Zap" size={16} />
                      <span className="text-sm font-semibold">{feature?.stats}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Study Tools */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Powerful Study Tools
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                Everything you need to excel in your Shakespeare studies, all in one place
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {studyTools?.map((tool, index) => (
                <div 
                  key={index}
                  className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-primary/30 transition-all duration-300 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
                    <Icon name={tool?.icon} size={28} className="text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{tool?.title}</h3>
                  <p className="text-white/60 text-sm">{tool?.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Student Testimonials */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8 mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Student Success Stories
              </h2>
              <p className="text-white/70 max-w-2xl mx-auto">
                Real students, real results, real improvement
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {studentTestimonials?.map((testimonial, index) => (
                <div 
                  key={index}
                  className="bg-surface-dark/50 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-primary/30 transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold text-lg">
                      {testimonial?.author?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{testimonial?.author}</p>
                      <p className="text-white/60 text-sm">{testimonial?.grade}</p>
                    </div>
                  </div>
                  <p className="text-white/80 italic mb-4">"{testimonial?.quote}"</p>
                  <div className="flex items-center gap-2 text-primary text-sm">
                    <Icon name="Award" size={16} />
                    <span>{testimonial?.achievement}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
            <div className="bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl p-8 md:p-12 border border-white/10 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-secondary/5 animate-pulse"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                  <Icon name="Rocket" size={40} className="text-primary" />
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Ready to Level Up Your Shakespeare Skills?
                </h2>
                <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                  Join thousands of students who are already mastering Shakespeare through gamified learning. Start your journey today!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link 
                    to="/registration"
                    className="px-8 py-4 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-colors duration-300 flex items-center justify-center gap-2"
                  >
                    Create Free Account
                    <Icon name="ArrowRight" size={20} />
                  </Link>
                  <Link 
                    to="/contact-us"
                    className="px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-colors duration-300 border border-white/20"
                  >
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default ForStudents;